import { computePathClustersGpu, isGpuPathClusteringAvailable } from './gpu/pathClusters';

const DEFAULT_MAX_ITER = 15;
const DEFAULT_TOLERANCE = 1e-3;

function sanitizeSamples(samples) {
  if (!Array.isArray(samples)) return [];
  const data = [];
  for (let i = 0; i < samples.length; i += 1) {
    const sample = samples[i];
    if (!sample) continue;
    const x = Number.isFinite(sample.x)
      ? sample.x
      : Array.isArray(sample) && Number.isFinite(sample[0]) ? sample[0] : null;
    const y = Number.isFinite(sample.y)
      ? sample.y
      : Array.isArray(sample) && Number.isFinite(sample[1]) ? sample[1] : null;
    if (x === null || y === null) continue;
    data.push([x, y]);
  }
  return data;
}

function computePathClustersCpu(samples, options = {}) {
  const data = sanitizeSamples(samples);
  if (!data.length) return [];
  const k = Math.max(1, Math.min(options.k || 4, data.length));
  const maxIterations = options.maxIterations || DEFAULT_MAX_ITER;
  const tolerance = options.tolerance || DEFAULT_TOLERANCE;

  const centers = [];
  const step = Math.max(1, Math.floor(data.length / k));
  for (let i = 0; i < k; i += 1) {
    centers.push([...data[(i * step) % data.length]]);
  }

  const assignments = new Array(data.length).fill(0);
  for (let iteration = 0; iteration < maxIterations; iteration += 1) {
    let converged = true;
    const sums = Array.from({ length: k }, () => ({ x: 0, y: 0, count: 0 }));

    for (let i = 0; i < data.length; i += 1) {
      const [px, py] = data[i];
      let bestIdx = 0;
      let bestDist = Infinity;
      for (let c = 0; c < k; c += 1) {
        const [cx, cy] = centers[c];
        const dx = px - cx;
        const dy = py - cy;
        const dist = dx * dx + dy * dy;
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = c;
        }
      }
      assignments[i] = bestIdx;
      const bucket = sums[bestIdx];
      bucket.x += px;
      bucket.y += py;
      bucket.count += 1;
    }

    for (let idx = 0; idx < k; idx += 1) {
      const bucket = sums[idx];
      if (bucket.count === 0) continue;
      const newX = bucket.x / bucket.count;
      const newY = bucket.y / bucket.count;
      const dx = centers[idx][0] - newX;
      const dy = centers[idx][1] - newY;
      if (dx * dx + dy * dy > tolerance) {
        converged = false;
      }
      centers[idx][0] = newX;
      centers[idx][1] = newY;
    }

    if (converged) break;
  }

  return centers.map(([x, y]) => ({ x, y }));
}

export function computePathClusters(samples, options = {}) {
  if (isGpuPathClusteringAvailable()) {
    try {
      const gpuResult = computePathClustersGpu(samples, options);
      if (Array.isArray(gpuResult) && gpuResult.length) {
        return gpuResult;
      }
    } catch (error) {
      console.warn('[GPU] Path clustering fallback to CPU:', error?.message || error);
    }
  }
  return computePathClustersCpu(samples, options);
}

export default computePathClusters;
